import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { RELATIVE_DATE_RANGES } from '../../../constants/dateRanges';
import {
  dateFilterDisplayFormat,
  dateFilterValueFormat,
  formatDateFilterForDisplay,
} from '../../../utils/dateFilterFormat';

dayjs.extend(customParseFormat);

// Day-first (Australian) typed formats accepted in the text field, with or
// without leading zeros — e.g. "12/6/2026" and "12/06/2026" both parse. When the
// field carries a time, the same day-first shapes are accepted with a trailing
// 24-hour time; a bare date still parses (its time defaults to midnight).
const TYPED_DATE_FORMATS = ['D/M/YYYY', 'DD/MM/YYYY', 'D/MM/YYYY', 'DD/M/YYYY'];
const TYPED_DATETIME_FORMATS = [
  'D/M/YYYY H:mm',
  'DD/MM/YYYY HH:mm',
  'D/M/YYYY HH:mm',
  'DD/MM/YYYY H:mm',
  ...TYPED_DATE_FORMATS,
];

const typedFormats = (withTime?: boolean): string[] =>
  withTime ? TYPED_DATETIME_FORMATS : TYPED_DATE_FORMATS;

export interface RelativeDateOption {
  label: string;
  value: string;
}

export const RELATIVE_OPTIONS = RELATIVE_DATE_RANGES as RelativeDateOption[];

/**
 * Resolve free text typed into the field to a stored value, or null when it
 * matches nothing. A day-first date string (e.g. "12/6/2026", or "12/6/2026
 * 09:00" for a with-time field) resolves to the wire format; otherwise the text
 * is matched against a relative option label (e.g. "first day of last month").
 */
export const resolveTypedValue = (text: string, withTime?: boolean): string | null => {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const parsed = dayjs(trimmed, typedFormats(withTime), true);
  if (parsed.isValid()) return parsed.format(dateFilterValueFormat(withTime));
  const relative = RELATIVE_OPTIONS.find(
    (option) => option.label.toLowerCase() === trimmed.toLowerCase()
  );
  return relative ? relative.value : null;
};

/**
 * Render a stored date-filter value for display. A value is either a relative
 * code (e.g. `first-day-of-last-month`, shown as its friendly label) or an
 * absolute date (shown as `DD/MM/YYYY`, plus `HH:mm` for with-time fields).
 * Unknown strings pass through unchanged so a saved filter never renders blank.
 */
export const formatDateFilterDisplay = (value: string | undefined, withTime?: boolean): string => {
  if (!value) return '';
  const relative = RELATIVE_OPTIONS.find((option) => option.value === value);
  if (relative) return relative.label;
  return formatDateFilterForDisplay(
    value,
    dateFilterValueFormat(withTime),
    dateFilterDisplayFormat(withTime)
  );
};
